import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import LibraryStats from '../components/library/LibraryStats';
import LibraryFilters from '../components/library/LibraryFilters';
import ModuleCard from '../components/library/ModuleCard';
import ModuleTable from '../components/library/ModuleTable';
import ModulePreviewModal from '../components/library/ModulePreviewModal';
import CreateModuleModal from '../components/library/CreateModuleModal';
import AssignToCampaignModal from '../components/library/AssignToCampaignModal';
import PageHeader from '../components/ui/PageHeader';
import ActionButton from '../components/ui/ActionButton';
import { dataFetch } from '../api';

const { FiPlus, FiBook, FiLoader, FiCheck, FiAlertCircle } = FiIcons;

const defaultFilters = { search: '', category: 'All', format: 'All', status: 'All', difficulty: 'All', compliance: 'All', language: 'All' };

const Library = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [view, setView] = useState('grid');
  const [previewModule, setPreviewModule] = useState(null);
  const [editModule, setEditModule] = useState(null);
  const [assignModule, setAssignModule] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    dataFetch('read/modules').then(res => {
      if (res?.status === 'success') {
        const parsed = (res.data || []).map(m => ({
          ...m,
          complianceTags: m.complianceTags ? JSON.parse(m.complianceTags) : [],
          assignedCampaigns: m.assignedCampaigns ? JSON.parse(m.assignedCampaigns) : []
        }));
        setModules(parsed);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const filtered = useMemo(() => modules.filter(m => {
    const s = filters.search.toLowerCase();
    const matchSearch = !s || (m.title||'').toLowerCase().includes(s) || (m.category||'').toLowerCase().includes(s) || (m.complianceTags||[]).some(t => t.toLowerCase().includes(s));
    const matchLang = filters.language === 'All' || (filters.language === 'English' && m.language === 'en') || (filters.language === 'Español' && m.language === 'es');
    return matchSearch && 
      (filters.category === 'All' || m.category === filters.category) && 
      (filters.format === 'All' || m.deliveryFormat === filters.format) && 
      (filters.status === 'All' || m.status === filters.status) && 
      (filters.difficulty === 'All' || m.difficultyLevel === filters.difficulty) && 
      (filters.compliance === 'All' || (m.complianceTags||[]).includes(filters.compliance)) && 
      matchLang;
  }), [modules, filters]);

  const handleCreate = newModule => { 
    setModules(prev => [newModule, ...prev]); 
    setShowCreate(false); 
  };

  const handleUpdate = updatedModule => {
    setModules(prev => prev.map(m => m.id === updatedModule.id ? updatedModule : m));
    setEditModule(null);
  };
  
  const handleArchive = async module => { 
    const newStatus = module.status === 'Archived' ? 'Active' : 'Archived';
    setModules(prev => prev.map(m => m.id === module.id ? { ...m, status: newStatus } : m)); 
    await dataFetch(`update/modules/${module.id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: newStatus, user_id: localStorage.getItem('user_id') })
    });
  };

  const handleAssign = (module, campaignIds) => { 
    setModules(prev => prev.map(m => m.id === module.id ? { 
      ...m, 
      assignedCampaigns: [...new Set([...(m.assignedCampaigns || []), ...campaignIds])], 
      usageCount: (m.usageCount || 0) + campaignIds.length 
    } : m)); 
  };

  const handleShare = async (module) => {
    if (!module || !module.id) return;
    
    // Generate a secure random token and set 24-hour expiration
    const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    // Format to YYYY-MM-DD HH:MM:SS for strict MySQL datetime compatibility
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 19).replace('T', ' ');
    
    try {
      // Get associated quiz questions to bundle with the share token
      const qRes = await dataFetch(`read/quiz_questions?module_id=${module.id}`);
      const quizQuestions = qRes?.data ? JSON.stringify(qRes.data) : '[]';

      // Store the share token in the DB
      const res = await dataFetch('create/module_share_tokens', {
        method: 'POST',
        body: JSON.stringify({
          token: token,
          module_id: Number(module.id),
          module_title: module.title || '',
          module_description: module.description || '',
          module_video_url: module.video_url || '',
          module_embedVideo: module.embedVideo || '',
          module_duration: module.duration || '',
          module_category: module.category || '',
          module_deliveryFormat: module.deliveryFormat || '',
          quiz_questions: quizQuestions,
          expires_at: expiresAt,
          user_id: localStorage.getItem('user_id') || 'system'
        })
      });

      if (res?.status === 'success') {
        const baseUrl = window.location.href.includes('#') 
          ? `${window.location.origin}${window.location.pathname}#/shared-module/${token}`
          : `${window.location.origin}/shared-module/${token}`;
          
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(baseUrl);
          setToast({ message: '24-hour module link copied to clipboard.', type: 'success' });
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = baseUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          setToast({ message: '24-hour module link copied to clipboard.', type: 'success' });
        }
      } else {
        setToast({ message: `Failed to generate share link. ${res?.error || ''}`, type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Error generating share link.', type: 'error' });
    }
  };
  
  const actions = { onPreview: setPreviewModule, onEdit: setEditModule, onArchive: handleArchive, onAssign: setAssignModule, onShare: handleShare };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto relative">
      <PageHeader
        eyebrow="Content"
        title="Training Library"
        description={loading ? 'Loading modules...' : `${filtered.length} of ${modules.length} modules`}
        action={<ActionButton icon={FiPlus} label="New Module" onClick={() => setShowCreate(true)} />}
      />

      <LibraryStats modules={modules} />
      <LibraryFilters filters={filters} setFilters={setFilters} view={view} setView={setView} />

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-20 text-slate-400">
            <SafeIcon icon={FiLoader} className="text-4xl mb-3 animate-spin" />
            <p className="font-semibold text-lg">Loading library...</p>
          </motion.div>
        ) : filtered.length === 0 ? (
          <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-20 text-slate-400">
            <SafeIcon icon={FiBook} className="text-5xl mb-3 opacity-30" />
            <p className="font-semibold text-lg">No modules found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </motion.div>
        ) : view === 'grid' ? (
          <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((m, i) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <ModuleCard module={m} {...actions} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div key="table" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ModuleTable modules={filtered} {...actions} />
          </motion.div>
        )}
      </AnimatePresence>

      {previewModule && <ModulePreviewModal module={previewModule} onClose={() => setPreviewModule(null)} onEdit={setEditModule} onAssign={setAssignModule} />}
      {showCreate && <CreateModuleModal onClose={() => setShowCreate(false)} onSave={handleCreate} />}
      {editModule && <CreateModuleModal initialData={editModule} onClose={() => setEditModule(null)} onSave={handleUpdate} />}
      {assignModule && <AssignToCampaignModal module={assignModule} onClose={() => setAssignModule(null)} onAssign={handleAssign} />}

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} 
            className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg border text-sm font-semibold bg-white text-slate-800 border-slate-200">
            {toast.type === 'error' ? <SafeIcon icon={FiAlertCircle} className="text-red-500 text-lg" /> : <SafeIcon icon={FiCheck} className="text-emerald-500 text-lg" />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Library;